import { useState } from 'react';
import {
  ArrowUpRight,
  ArrowDownRight,
  Repeat,
  CreditCard,
  Pencil,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Rocket,
} from 'lucide-react';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import EmptyState from '@components/common/EmptyState';
import { formatCurrency, formatDate } from '@utils/formatters';
import { clsx } from 'clsx';

const TransactionTimeline = ({
  items,
  onEdit,
  onDelete,
  onPayInstallment,
  onLaunchRecurrence,
  selectedDate,
}) => {
  const { accounts } = useAccounts();
  const { categories } = useCategories();
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [launchingId, setLaunchingId] = useState(null);

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.accountId === accountId);
    return account ? `${account.icon} ${account.name}` : '';
  };

  const getCategoryInfo = (categoryId) => {
    const category = categories.find(c => c.categoryId === categoryId);
    return category || { name: 'Sem categoria', icon: '\uD83D\uDCC1', color: '#6B7280' };
  };

  const getSourceLabel = (item) => {
    switch (item.source) {
      case 'recurrence':
        return 'Recorrencia';
      case 'installment':
        return `Parcela ${item.installmentNumber}/${item.totalInstallments}`;
      default:
        return null;
    }
  };

  const handleLaunch = async (item) => {
    if (launchingId) return;
    setLaunchingId(item.id);
    try {
      await onLaunchRecurrence?.(item);
    } finally {
      setLaunchingId(null);
    }
  };

  // Group items by date
  const groupedItems = items.reduce((groups, item) => {
    const date = item.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedItems).sort((a, b) => b.localeCompare(a));

  const toggleGroup = (date) => {
    const next = new Set(collapsedGroups);
    if (next.has(date)) {
      next.delete(date);
    } else {
      next.add(date);
    }
    setCollapsedGroups(next);
  };

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Clock size={32} />}
        title="Nenhuma movimentacao"
        description="Nao ha transacoes no periodo selecionado"
      />
    );
  }

  // Compute running daily balance (cumulative net per day, descending order display)
  const dailyNet = {};
  sortedDates.forEach((date) => {
    dailyNet[date] = groupedItems[date].reduce((sum, item) => sum + item.amount, 0);
  });

  // Accumulate from oldest to newest for running balance
  const sortedAsc = [...sortedDates].reverse();
  const runningBalances = {};
  let cumulative = 0;
  sortedAsc.forEach((date) => {
    cumulative += dailyNet[date];
    runningBalances[date] = cumulative;
  });

  return (
    <div className="relative pl-8 md:pl-12">
      {/* Vertical timeline line */}
      <div className="absolute left-3 md:left-5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />

      <div className="space-y-8">
        {sortedDates.map((date, dateIndex) => {
          const dateItems = groupedItems[date];
          const isCollapsed = collapsedGroups.has(date);
          const dayTotal = dailyNet[date];
          const balance = runningBalances[date];
          const pendingCount = dateItems.filter(i => i.status === 'pending').length;
          const isLast = dateIndex === sortedDates.length - 1;

          return (
            <div key={date} className="relative">
              {/* Date marker circle on the timeline */}
              <div
                className={clsx(
                  'absolute -left-8 md:-left-12 top-0 flex items-center justify-center',
                  'w-6 h-6 md:w-10 md:h-10 rounded-full border-2 z-10',
                  date === selectedDate
                    ? 'bg-primary-100 border-primary-500 dark:bg-primary-900/40 dark:border-primary-400'
                    : 'bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-600'
                )}
              >
                <span className="hidden md:block text-[10px] font-bold text-gray-600 dark:text-gray-300">
                  {new Date(date + 'T12:00:00').getDate()}
                </span>
              </div>

              {/* Date header */}
              <button
                onClick={() => toggleGroup(date)}
                className={clsx(
                  'w-full flex items-center justify-between px-4 py-3 rounded-t-2xl transition-colors',
                  'bg-gray-50 dark:bg-gray-800/80 hover:bg-gray-100 dark:hover:bg-gray-700/80',
                  date === selectedDate && 'ring-2 ring-primary-400/50',
                  isCollapsed && 'rounded-b-2xl'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm md:text-base">
                      {formatDate(date, 'full')}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {dateItems.length} transacao(oes)
                        {pendingCount > 0 && ` \u00B7 ${pendingCount} prevista(s)`}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Day net total */}
                  <div className="text-right">
                    <p className={clsx(
                      'text-sm font-semibold',
                      dayTotal >= 0 ? 'text-emerald-600' : 'text-red-600'
                    )}>
                      {dayTotal >= 0 ? '+' : ''}{formatCurrency(dayTotal)}
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                      Saldo: <span className={clsx(
                        'font-medium',
                        balance >= 0 ? 'text-emerald-500' : 'text-red-500'
                      )}>
                        {formatCurrency(balance)}
                      </span>
                    </p>
                  </div>
                  <ChevronDown
                    size={18}
                    className={clsx(
                      'text-gray-400 transition-transform duration-200',
                      !isCollapsed && 'rotate-180'
                    )}
                  />
                </div>
              </button>

              {/* Transaction cards */}
              {!isCollapsed && (
                <div className="bg-white dark:bg-gray-800 rounded-b-2xl border border-t-0 border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
                  {dateItems.map((item) => {
                    const category = getCategoryInfo(item.categoryId);
                    const isPending = item.status === 'pending';
                    const isIncome = item.type === 'income';
                    const sourceLabel = getSourceLabel(item);

                    return (
                      <div
                        key={item.id}
                        className={clsx(
                          'relative flex items-center justify-between p-4 group transition-colors',
                          'hover:bg-gray-50 dark:hover:bg-gray-700/30',
                          isPending && 'border-l-2 border-dashed border-yellow-400'
                        )}
                      >
                        {/* Left accent bar */}
                        {!isPending && (
                          <div className={clsx(
                            'absolute left-0 top-0 bottom-0 w-1 rounded-r',
                            isIncome ? 'bg-emerald-500' : 'bg-red-500'
                          )} />
                        )}

                        <div className="flex items-center gap-3 min-w-0 pl-2">
                          {/* Type icon */}
                          <div className={clsx(
                            'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center relative',
                            isIncome
                              ? 'bg-emerald-50 dark:bg-emerald-900/20'
                              : 'bg-red-50 dark:bg-red-900/20'
                          )}>
                            {isIncome ? (
                              <ArrowUpRight className="text-emerald-600" size={18} />
                            ) : (
                              <ArrowDownRight className="text-red-600" size={18} />
                            )}
                            {isPending && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                                <Clock size={9} className="text-yellow-800" />
                              </div>
                            )}
                          </div>

                          {/* Details */}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={clsx(
                                'font-medium text-sm truncate',
                                isPending
                                  ? 'text-gray-400 dark:text-gray-500'
                                  : 'text-gray-900 dark:text-white'
                              )}>
                                {item.description}
                              </p>
                              {sourceLabel && (
                                <Badge variant="default" size="sm">
                                  {item.source === 'recurrence' && <Repeat size={10} className="mr-1 text-purple-500" />}
                                  {item.source === 'installment' && <CreditCard size={10} className="mr-1 text-orange-500" />}
                                  {sourceLabel}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span
                                className="text-[11px] px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: `${category.color}20`, color: category.color }}
                              >
                                {category.icon} {category.name}
                              </span>
                              <span className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                                {getAccountName(item.accountId)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Amount + actions */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <p className={clsx(
                              'font-semibold text-sm',
                              isIncome ? 'text-emerald-600' : 'text-red-600',
                              isPending && 'opacity-60'
                            )}>
                              {isIncome ? '+' : ''}{formatCurrency(item.amount)}
                            </p>
                            {isPending && (
                              <p className="text-[10px] text-yellow-600 dark:text-yellow-500 flex items-center justify-end gap-0.5">
                                <Clock size={9} />
                                Previsto
                              </p>
                            )}
                          </div>

                          {/* Hover actions */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {item.source === 'transaction' && (
                              <>
                                <button
                                  onClick={() => onEdit?.(item)}
                                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                  title="Editar"
                                >
                                  <Pencil size={14} className="text-gray-400" />
                                </button>
                                <button
                                  onClick={() => onDelete?.(item)}
                                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                  title="Excluir"
                                >
                                  <Trash2 size={14} className="text-red-500" />
                                </button>
                              </>
                            )}
                            {item.source === 'installment' && isPending && (
                              <Button
                                size="sm"
                                onClick={() => onPayInstallment?.(item)}
                              >
                                <Check size={12} className="mr-1" />
                                Pagar
                              </Button>
                            )}
                            {item.source === 'recurrence' && isPending && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleLaunch(item)}
                                isLoading={launchingId === item.id}
                                disabled={!!launchingId}
                              >
                                <Rocket size={12} className="mr-1" />
                                Lancar
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TransactionTimeline;
